import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import { toast } from 'react-toastify'

import useSession from '@/hooks/useSession'
import { registerWithPassword } from '@/services/contribution/api.tsx'
import { isEmailValid } from '@/util/helpers'

import { sendAuthErrorEvent, sendRegisterPasswordEvent } from '@/services/analytics/gtm'
import Button from '@components/Button'
import Input from '@components/Input'

interface RegistrationFormProps {
  schoolSlug?: string
}

export default function RegistrationForm({ schoolSlug }: RegistrationFormProps) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [touched, setTouched] = useState({ email: false, password: false })
  const [isPending, setIsPending] = useState(false)

  const { startSession } = useSession()
  const navigate = useNavigate()

  const invalidEmail = useMemo(() => !isEmailValid(email), [email])
  const passwordTooShort = password.length > 0 && password.length < 8
  const passwordTooLong = password.length > 16
  const passwordMissingNumber = password.length > 0 && !/\d/.test(password)
  const invalidPassword = passwordTooShort || passwordTooLong || passwordMissingNumber

  const isValid = name.trim().length > 0 && email.trim().length > 0 && !invalidEmail && !invalidPassword

  async function onRegisterWithPassword() {
    try {
      setIsPending(true)
      if (!isValid) {
        throw new Error('All fields are required to create an account.')
      }
      if (!schoolSlug) {
        throw new Error('Required parameters are missing')
      }

      const { accessToken, eventId, participationId, parentName, parentSlug, schoolName, isParticipationMutable } =
        await registerWithPassword(schoolSlug, name, email, password)

      startSession({
        accessToken,
        eventId: eventId.toString(),
        participationId: participationId.toString(),
        parentName,
        parentSlug,
        schoolName: schoolName ?? '',
        schoolSlug,
        isParticipationMutable,
      })

      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Registered with Password',
        level: 'info',
        data: {
          participationId,
          parentName,
          parentSlug,
          schoolName,
          schoolSlug,
        },
      })

      sendRegisterPasswordEvent()
      navigate(`/${schoolSlug}/onboarding`)
      return true
    } catch (error) {
      let toastMessage = t('content.components.registration.error_toast')

      if (error instanceof Error) {
        let errorCause: string
        switch (error.message) {
          case 'Account Already Exists.':
            toastMessage = t('content.components.registration.error_duplicate_account_toast')
            errorCause = 'account_already_exists'
            break
          case 'Registration Closed.':
            toastMessage = `We're sorry. Registration has closed for this event.`
            errorCause = 'registration_closed'
            break
          default:
            toastMessage += ` ${error.message}`
            errorCause = error.message
        }
        sendAuthErrorEvent({ action: 'register_password', errorCause })
        Sentry.captureException(error, {
          tags: {
            location: 'Register with Password',
          },
          extra: {
            schoolSlug,
            email,
            errorMessage: toastMessage,
          },
        })
      }

      toast.error(toastMessage, {
        autoClose: false,
      })
      return false
    }
  }

  return (
    <div className='flex flex-col gap-4 justify-center items-center h-full pt-4'>
      <div className='w-full'>
        <Input
          id='name'
          type='text'
          label={t('content.components.registration.name_field_title')}
          value={name}
          labelStyle='text-cta'
          onInput={setName}
          placeholder={t('content.components.registration.name_field_placeholder')}
        />
      </div>

      <div className='w-full'>
        <Input
          id='email'
          type='email'
          label={t('content.components.registration.email_field_title')}
          value={email}
          labelStyle='text-cta'
          onInput={setEmail}
          placeholder={t('content.components.input_contact.email_field_placeholder')}
          error={touched.email && invalidEmail}
          onBlur={() => setTouched((t) => ({ ...t, email: true }))}
        />
        {touched.email && invalidEmail && (
          <p className='mt-1 text-sm text-red-500'>{t('content.components.registration.validation.email')}</p>
        )}
      </div>

      <div className='w-full mb-4'>
        <Input
          id='password'
          type='password'
          autocomplete='new-password'
          label={t('content.components.signin.password.password_field_title')}
          value={password}
          labelStyle='text-cta'
          onInput={setPassword}
          placeholder={t('content.components.registration.password_field_placeholder')}
          error={touched.password && invalidPassword}
          onBlur={() => setTouched((t) => ({ ...t, password: true }))}
        />
        {touched.password && passwordTooShort && (
          <p className='mt-1 text-sm text-red-500'>
            {t('content.components.registration.validation.password_too_short')}
          </p>
        )}
        {touched.password && passwordTooLong && (
          <p className='mt-1 text-sm text-red-500'>
            {t('content.components.registration.validation.password_too_long')}
          </p>
        )}
        {touched.password && !passwordTooShort && !passwordTooLong && passwordMissingNumber && (
          <p className='mt-1 text-sm text-red-500'>
            {t('content.components.registration.validation.password_missing_number')}
          </p>
        )}
      </div>

      <Button
        variant='cta'
        label={t('content.components.registration.register_button_title')}
        fullwidth
        onClick={onRegisterWithPassword}
        disabled={!isValid || isPending}
      />
    </div>
  )
}
